import React, { PureComponent } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { ProductCard } from '@gravity/product';
import { ProductModel, ProductType } from '@nucleo/product';
import WidgetState from '@gravity/widget-state';
import { FrontEndError } from '@gravity/g-axios';

const Wrapper = styled.div.attrs({
  className: 'stx-Product',
})`
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 100%;
  vertical-align: baseline;
  box-sizing: border-box;
  text-align: left;
`;

/**
 * The product widget displays a product
 */
class ProductWidget extends PureComponent {
  componentDidMount() {
    this.props.load();
  }
  componentDidCatch(error, info) {
    // You can also log the error to an error reporting service
    this.props.logError(FrontEndError.RenderWidgetError, error, info);
  }
  render() {
    const productData = this.props.product;
    const { LOADING, ERROR } = productData;
    const hasData = ProductModel.hasData(productData);
    const occId = this.props.occurrenceId;
    // if an occurrenceId is passed, try to load it
    const performance =
      hasData && occId && (productData.performances || []).find(perf => perf.id === occId);
    const timeslot =
      hasData && occId && (productData.timeslots || []).find(slot => slot.id === occId);
    let curState;
    if (ERROR || this.props.configError) {
      curState = 'ERROR';
    } else if (LOADING) {
      curState = 'LOADING';
    }
    return (
      <Wrapper>
        <WidgetState state={curState} code={this.props.configErrorCode} />
        {hasData && (
          <ProductCard
            product={productData}
            performance={performance}
            timeslot={timeslot}
            showActions={this.props.showActions}
            showImage={this.props.showImage}
            showOccurrences={this.props.showOccurrences}
            showTitle={this.props.showTitle}
            aspectRatio={this.props.aspectRatio}
            showDescription={this.props.showDescription}
            showPrice={this.props.showPrice}
            currencyCode={this.props.currencyCode}
          />
        )}
      </Wrapper>
    );
  }
}

ProductWidget.propTypes = {
  /**
   * Global config : contains the point of sales that will provide information. To be passed in the STX.widgets.start function.
   */
  hostname: PropTypes.string.isRequired,
  /**
   * Global config : contains the api key related to the point of sales. To be passed in the STX.widgets.start function.
   */
  apiKey: PropTypes.string.isRequired,
  /**
   * Global config : Explicitely pass the language to the widget that will be loaded. If not passed, the default language of the institution will be used
   */
  language: PropTypes.string,
  /**
   * The product id to display
   */
  productId: PropTypes.number.isRequired,
  /**
   * The occurrence id to display. It corresponds to a timeslot, performance or match id dependingon the product. If provided, the information displayed -
   * name, description, image, etc.. - will be first those from the occurence, if exists. If specific data do not exist, the product data will be shown.
   */
  occurrenceId: PropTypes.number,
  /**
   * The Aspect ratio for the image.
   */
  aspectRatio: PropTypes.oneOf(['VERTICAL', 'HORIZONTAL', 'WIDE', 'SQUARE', 'LANDSCAPE']),
  /**
   * If true, the widget will display buttons at the bottom to redirect to the sell, reserver, etc.. actions, depending of available actions.
   */
  showActions: PropTypes.bool,
  /**
   * Allows to hide/show the image. If no image define and the parameter is true, then a default image will be shown
   */
  showImage: PropTypes.bool,
  /**
   * In case of visit pass that have timeslots, it is possible to show a list of slots as buttons. It redirects to the corresponding ticket shop page
   */
  showOccurrences: PropTypes.bool,
  /**
   * Hide or show the product/occurence description if provided
   */
  showDescription: PropTypes.bool,
  /**
   * Hide or show the minimum price info if provided
   */
  showPrice: PropTypes.bool,
  /**
   * Show the product/occurence title and subtitle
   */
  showTitle: PropTypes.bool,
  /**
   * @IGNORE Contains the currency in which the price should be displayed, generated by config load
   */
  currencyCode: PropTypes.string.isRequired,
  /**
   *  @IGNORE generated, define if an error happend at the config level.
   */
  configError: PropTypes.bool,
  /**
   * @IGNORE generated, define what kind of error happened at the config level
   */
  configErrorCode: PropTypes.string,
  /**
   * @IGNORE generated product after a load of data
   */
  product: ProductType,
  /**
   * @IGNORE generated function to load the catalog dated
   */
  load: PropTypes.func.isRequired,
  /**
   * @IGNORE generated function to log in case of error
   */
  logError: PropTypes.func.isRequired,
};
ProductWidget.defaultProps = {
  aspectRatio: 'VERTICAL',
  product: null,
  showActions: true,
  showImage: true,
  showOccurrences: false,
  showTitle: true,
  showDescription: false,
  showPrice: false,
  occurrenceId: null,
  configError: false,
  configErrorCode: null,
};

export default ProductWidget;
